// config/firebase.js
// BuzzInApp — Firebase initialization
// Proyecto: boletin-oficial-ar | Site: buzzinapp-b2d1a

import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  PhoneAuthProvider,
  signInWithPopup,
  signInWithPhoneNumber,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  serverTimestamp,
  increment,
  Timestamp,
} from "firebase/firestore";

// ─── Firebase config ────────────────────────────────────────────────────────
const firebaseConfig = {
  apiKey: "AIzaSyAv7nEV5dCYtjO8xLo0DXPw47WXn8I34NI",
  authDomain: "boletin-oficial-ar.firebaseapp.com",
  projectId: "boletin-oficial-ar",
  storageBucket: "boletin-oficial-ar.firebasestorage.app",
  messagingSenderId: "424089107518",
  appId: "1:424089107518:web:53ddf58033f7ca8118256f",
};

// ─── App instance (singleton) ────────────────────────────────────────────────
const app = initializeApp(firebaseConfig);

// ─── Auth ────────────────────────────────────────────────────────────────────
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();
export const phoneProvider = new PhoneAuthProvider(auth);

// Pre-configure Google provider
googleProvider.setCustomParameters({ prompt: "select_account" });

// ─── Firestore ───────────────────────────────────────────────────────────────
export const db = getFirestore(app);

// ─── Collection references ───────────────────────────────────────────────────
// Centralizar las referencias evita errores de typo en toda la app
export const COLLECTIONS = {
  USERS:       "users",
  CLICKS:      "clicks",
  REDEMPTIONS: "redemptions",
  DISPUTES:    "disputes",
  BUSINESSES:  "businesses", // mirror de Google Sheet para datos en tiempo real
};

export const usersCol      = () => collection(db, COLLECTIONS.USERS);
export const clicksCol     = () => collection(db, COLLECTIONS.CLICKS);
export const redemptionsCol= () => collection(db, COLLECTIONS.REDEMPTIONS);
export const disputesCol   = () => collection(db, COLLECTIONS.DISPUTES);

// ─── Auth helpers ────────────────────────────────────────────────────────────

/**
 * Inicia sesión con Google popup.
 * @returns {Promise<import("firebase/auth").UserCredential>}
 */
export const signInWithGoogle = () => signInWithPopup(auth, googleProvider);

/**
 * Envía el código SMS al número indicado.
 * @param {string} phoneNumber  Formato E.164: +5491112345678
 * @param {import("firebase/auth").ApplicationVerifier} appVerifier  reCAPTCHA verifier
 * @returns {Promise<import("firebase/auth").ConfirmationResult>}
 */
export const sendSmsCode = (phoneNumber, appVerifier) =>
  signInWithPhoneNumber(auth, phoneNumber, appVerifier);

/**
 * Cierra sesión del usuario actual.
 */
export const logOut = () => signOut(auth);

/**
 * Suscripción al estado de autenticación.
 * @param {(user: import("firebase/auth").User | null) => void} callback
 * @returns {() => void} unsubscribe
 */
export const onAuthChange = (callback) => onAuthStateChanged(auth, callback);

// ─── Firestore helpers ────────────────────────────────────────────────────────

/**
 * Lee un documento por colección + id.
 * @param {string} collectionName
 * @param {string} docId
 * @returns {Promise<object|null>}
 */
export const getDocument = async (collectionName, docId) => {
  const snap = await getDoc(doc(db, collectionName, docId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/**
 * Escribe (merge) un documento. Crea si no existe.
 * @param {string} collectionName
 * @param {string} docId
 * @param {object} data
 */
export const setDocument = (collectionName, docId, data) =>
  setDoc(doc(db, collectionName, docId), data, { merge: true });

/**
 * Actualiza campos específicos de un documento existente.
 */
export const updateDocument = (collectionName, docId, data) =>
  updateDoc(doc(db, collectionName, docId), data);

/**
 * Agrega un documento nuevo con ID auto-generado.
 * @returns {Promise<string>} ID del documento creado
 */
export const addDocument = async (collectionName, data) => {
  const ref = await addDoc(collection(db, collectionName), {
    ...data,
    createdAt: serverTimestamp(),
  });
  return ref.id;
};

// ─── Re-exports de utilidades de Firestore ───────────────────────────────────
// Los hooks y pantallas importan desde acá para mantener una sola dependencia
export {
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  serverTimestamp,
  increment,
  Timestamp,
  doc,
  collection,
};

export default app;
